// components/forms/OTPForm.js
import React, { useState, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import NeumorphicInputOtp from '../../../../assets/components/auth/NeumorphicInputOtp';
import NeumorphicButtonShort from '../../../../assets/components/auth/NeumorphicButtonShort';
import NeumorphicDivider from '../../../../assets/components/auth/NeumorphicDivider';
import { useAuth } from '../../hooks/useAuth';
import { otpUtils } from '../../utils/otpUtils';
import { secureStorage } from '../../../../shared/utils/secureStorage';

const OTPForm = memo(({ onSwitchToLogin, email: propEmail, onVerified, onOrgSuspended }) => {
  const navigate = useNavigate();
  const { verifyOtp, resendOtp, loading, error, clearError } = useAuth();

  const inputRefs = useRef([]);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [email, setEmail] = useState(propEmail || '');
  const [localError, setLocalError] = useState('');
  const [hasFailedOnce, setHasFailedOnce] = useState(false);

  // Initialize input refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Get email from encrypted OTP session if not provided as prop
  useEffect(() => {
    if (!email) {
      const otpSession = secureStorage.getOTPSession();
      if (!otpSession || !otpSession.email) {
        navigate('/', { replace: true });
        return;
      }
      setEmail(otpSession.email);
    }
  }, [navigate, email]);

  // Timer countdown - only start after first OTP is sent
  useEffect(() => {
    let interval = null;
    if (timer > 0 && timer < 60) { // Only count down if timer was started
      interval = setInterval(() => {
        setTimer(seconds => seconds - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Handle OTP input changes
  const handleChange = (index, e) => {
    const value = e.target.value;

    // Only allow digits
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = otpUtils.handleOtpChange(index, value, otp, inputRefs.current);
    if (newOtp) {
      setOtp(newOtp);

      // Auto-verify if all fields are filled (only on first attempt or after resend)
      if (newOtp.every(digit => digit) && index === 5 && !hasFailedOnce) {
        const timerId = setTimeout(() => {
          handleVerify(newOtp);
        }, 300);

        // Cleanup not needed - verify happens quickly and component remains mounted
        // If component unmounts, verification won't proceed as state is cleared
      }
    }
  };

  // Handle key down events
  const handleKeyDown = (index, e) => {
    const newOtp = otpUtils.handleOtpKeyDown(index, e, otp, inputRefs.current);
    if (newOtp) {
      setOtp(newOtp);
    }
    
    // If Enter is pressed and all OTP fields are filled, submit
    if (e.key === 'Enter' && otp.every(digit => digit !== '')) {
      handleVerify();
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    const newOtp = otpUtils.handleOtpPaste(pastedData, otp, inputRefs.current);
    if (newOtp) {
      setOtp(newOtp);

      // Auto-verify if all fields are filled from paste (only if not failed before)
      if (newOtp.every(digit => digit) && !hasFailedOnce) {
        const timerId = setTimeout(() => {
          handleVerify(newOtp);
        }, 300);

        // Cleanup not needed - verify happens quickly and component remains mounted
        // If component unmounts, verification won't proceed as state is cleared
      }
    }
  };

  // Verify OTP
  const handleVerify = async (otpToVerify = null) => {
    const currentOtp = otpToVerify || otp;

    // Clear any existing errors
    setLocalError('');
    clearError();

    // Validate complete OTP before proceeding
    if (currentOtp.some(digit => !digit)) {
      setLocalError('Please enter the complete 6-digit OTP');
      return;
    }

    const otpString = currentOtp.join('');
    const result = await verifyOtp(email, otpString);

    if (result.success) {
      onVerified?.();
    } else if (result.orgSuspended) {
      onOrgSuspended?.({
        organisationName: result.organisationName,
        suspendedAt: result.suspendedAt
      });
    } else {
      setHasFailedOnce(true);
      setOtp(['', '', '', '', '', '']);
      setLocalError(result.error || 'Incorrect code. Please request a new one.');
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  };

  // Resend OTP - only when user explicitly clicks the button
  const handleResendOtp = async () => {
    clearError();
    setLocalError('');

    // Call resend with explicit resend flag
    const result = await resendOtp(email, true); // Pass flag to indicate this is a manual resend

    if (result.success) {
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();

      // Reset timer and states
      setTimer(60);
      setCanResend(false);
      setHasFailedOnce(false); // Reset failure state since we got a new OTP

      setLocalError('New OTP sent to your email.');

      // Clear the success message after 3 seconds
      const timerId = setTimeout(() => {
        setLocalError('');
      }, 3000);

      // Cleanup not strictly needed - component remains mounted during normal flow
      // Message will clear naturally if component unmounts
    } else {
      setLocalError(result.error || 'Failed to resend OTP. Please try again.');
    }
  };

  const displayError = localError;

  return (
    <div className="otp-form-container">
      <h2 className="login-form-title login-heading">Verification Code</h2>
      
      <p className="otp-form-description">
        We've sent a 6-digit code to <span className="form-email">{email || 'your email'}</span>. 
        <br/>
        Enter the code below to verify your account.
      </p>

      <div className="otp-form-inputs">
        {otp.map((digit, index) => (
          <NeumorphicInputOtp
            key={`otp-digit-${index}`}
            ref={el => inputRefs.current[index] = el}
            type="text"
            maxLength={1}
            placeholder="-"
            className="otp-form-input"
            value={digit}
            onChange={e => handleChange(index, e)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : null}
            autoFocus={index === 0}
            disabled={loading}
          />
        ))}
      </div>

      <div className="otp-feedback-area">
        {displayError && (
          <div className="error-message" style={{
            color: displayError.includes('New OTP sent') ? 'green' : 'red',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {displayError}
          </div>
        )}

        <div className="otp-form-timer">
          {(hasFailedOnce || canResend || timer === 0) ? (
            <p className="otp-form-resend-text">
              {!hasFailedOnce && 'Need a new code? '}
              <button
                className="otp-form-resend-button"
                onClick={handleResendOtp}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend OTP?'}
              </button>
            </p>
          ) : timer < 60 ? (
            <p>Resend code in <span className="otp-timer">{timer}s</span></p>
          ) : (
            <p>Check your email for the verification code</p>
          )}
        </div>
      </div>

      <NeumorphicDivider widthPercentage="90"></NeumorphicDivider>

      <div className="otp-form-buttons">
        <NeumorphicButtonShort
          type="button"
          onClick={onSwitchToLogin}
          disabled={loading}
        >
          Back
        </NeumorphicButtonShort>

        <NeumorphicButtonShort
          onClick={() => handleVerify()}
          disabled={otp.some(digit => !digit) || loading}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </NeumorphicButtonShort>
      </div>
    </div>
  );
});

OTPForm.displayName = 'OTPForm';

OTPForm.propTypes = {
  onSwitchToLogin: PropTypes.func.isRequired,
  email: PropTypes.string,
  onVerified: PropTypes.func,
  onOrgSuspended: PropTypes.func,
};

OTPForm.defaultProps = {
  email: '',
};

// Custom comparison to prevent re-renders when function/email references are the same
const arePropsEqual = (prevProps, nextProps) => {
  return prevProps.onSwitchToLogin === nextProps.onSwitchToLogin &&
         prevProps.email === nextProps.email;
};

export default memo(OTPForm, arePropsEqual);